import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonSpinner,
  IonText,
  IonButton,
  IonRefresher,
  IonRefresherContent,
  useIonRouter,
  type RefresherCustomEvent
} from '@ionic/react';
import { useUserAccount } from '../../../hooks/useUserAccount';
import { useQuery } from '@apollo/client/react';
import { GET_MY_FANTASY_LEAGUES } from '../../../graphql/queries/myFantasyLeagues';
import type {
  GetMyFantasyLeaguesQuery,
  GetMyFantasyLeaguesQueryVariables,
} from '../../../types/graphql-generated';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { FantasyLeagueCard } from '../components/fantasy-league-card/FantasyLeagueCard';
import { mapMemberToCardData } from '../components/fantasy-league-card/mappers';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const MY_LEAGUES_PATH = '/app/main/mis-ligas';

export const MyLeaguesTab = () => {
  const { userAccount, loading: accountLoading, error: accountError } = useUserAccount();
  const {
    data,
    loading: leaguesLoading,
    error: leaguesError,
    refetch,
  } = useQuery<GetMyFantasyLeaguesQuery, GetMyFantasyLeaguesQueryVariables>(
    GET_MY_FANTASY_LEAGUES,
    {
      skip: !userAccount,
      fetchPolicy: 'cache-and-network',
    }
  );
  const router = useIonRouter();
  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);

  // Ionic may keep this tab mounted while a league is open. Revalidate when
  // navigating back so server-computed notification counts cannot stay stale.
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = pathname;

    if (
      pathname === MY_LEAGUES_PATH &&
      previousPathname !== MY_LEAGUES_PATH &&
      userAccount
    ) {
      void refetch().catch(() => undefined);
    }
  }, [pathname, refetch, userAccount]);

  const handleRefresh = async (event: RefresherCustomEvent) => {
    try {
      await refetch();
    } finally {
      event.detail.complete();
    }
  };

  const members = data?.userAccount?.fantasyLeagueMembers || [];
  // Only take over the whole tab with a spinner on the very first load. During a
  // pull-to-refresh Apollo flips `loading` back to true, but we keep the existing
  // content mounted so the screen doesn't flash blank — the refresher shows its
  // own spinner instead. This mirrors how MarketTab keeps its shell mounted.
  const showInitialLoading = accountLoading || (leaguesLoading && !data);
  const error = accountError || leaguesError;

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar className="px-4">
          <IonTitle>Mis Ligas</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {showInitialLoading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        ) : error ? (
          <div className="ion-padding">
            <IonText color="danger">
              <p>Error al cargar las ligas</p>
            </IonText>
          </div>
        ) : !userAccount ? (
          <div className="ion-padding">
            <IonText>
              <p>No se encontró información del usuario</p>
            </IonText>
          </div>
        ) : (
          <>
            {/* Action buttons */}
            <div className="flex gap-2 px-4 pt-4">
              <IonButton
                className="flex-1"
                fill="solid"
                onClick={() => router.push('/app/ligas/crear')}
              >
                Crear
              </IonButton>
              <IonButton
                className="flex-1"
                fill="solid"
                onClick={() => router.push('/app/main/mis-ligas/buscar')}
              >
                Unirse
              </IonButton>
            </div>

            {/* League list */}
            {members.length === 0 ? (
              <div className="flex flex-col justify-center items-center min-h-[200px] text-center px-4 mt-4">
                <IonText color="medium">
                  <p>No estás en ninguna liga todavía</p>
                  <p className="text-sm mt-1">Crea una nueva liga o únete a una existente</p>
                </IonText>
              </div>
            ) : (
              <div className="mt-4 pb-4">
                {members.map((member) => {
                  const cardData = mapMemberToCardData(member);
                  return (
                    <FantasyLeagueCard
                      key={cardData.id}
                      league={cardData}
                      onAction={() => router.push(`/app/ligas/${cardData.id}/`)}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};
